// Cookie helper functions
export const setCookie = (name: string, value: string) => {
    try {
        // Always set cookie for root domain to share across subdomains
        const domainParts = window.location.hostname.split('.')
        let rootDomain

        // Handle localhost and IP addresses
        if (domainParts.length <= 2 || domainParts.includes('localhost') || domainParts[0].match(/^\d+$/)) {
            rootDomain = window.location.hostname
        } else {
            // Get the root domain (e.g., 'commzone.app' from 'app.commzone.app')
            rootDomain = domainParts.slice(-2).join('.')
        }

        // Set cookie with explicit domain and path
        const cookieString = `${name}=${value}; path=/; max-age=2592000; SameSite=Lax; domain=.${rootDomain}`
        document.cookie = cookieString

        // Map cookie names to storage keys
        const storageKey =
            name === 'idev_id'
                ? STORAGE_KEYS.IDEV_ID
                : name === 'idev_username'
                  ? STORAGE_KEYS.IDEV_USERNAME
                  : `affiliate_${name}`

        // Also set in localStorage as backup
        try {
            localStorage.setItem(storageKey, value)
            // Attempt to set sessionStorage as well for immediate cross-tab access
            sessionStorage.setItem(storageKey, value)
        } catch (e) {
            console.warn(`Could not set storage for ${name}:`, e)
        }
    } catch (error) {
        console.error(`Error setting cookie ${name}:`, error)
    }
}

export const getCookie = (name: string): string | null => {
    try {
        // Try cookie first
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) {
            const result = parts.pop()?.split(';').shift() || null
            return result
        }

        // Map cookie names to storage keys
        const storageKey =
            name === 'idev_id'
                ? STORAGE_KEYS.IDEV_ID
                : name === 'idev_username'
                  ? STORAGE_KEYS.IDEV_USERNAME
                  : `affiliate_${name}`

        // Try sessionStorage next (for immediate cross-tab access)
        const sessionValue = sessionStorage.getItem(storageKey)
        if (sessionValue) {
            // Set it as a cookie to persist it
            setCookie(name, sessionValue)
            return sessionValue
        }

        // Try localStorage as final backup
        const localValue = localStorage.getItem(storageKey)
        if (localValue) {
            // Set it as a cookie to persist it
            setCookie(name, localValue)
            return localValue
        }

        return null
    } catch (error) {
        console.error(`Error getting cookie ${name}:`, error)
        return null
    }
}

// Constants for storage keys
const STORAGE_KEYS = {
    IDEV_ID: 'affiliate_idev_id',
    IDEV_USERNAME: 'affiliate_idev_username',
    CUSTOMER_IP: 'affiliate_customer_ip',
    IP_DATA: 'affiliate_ip_data',
}

// Verification function to check data accessibility
const verifyAffiliateData = () => {
    const allData = {
        cookies: {
            raw: document.cookie,
            idev_id: getCookie('idev_id'),
            idev_username: getCookie('idev_username'),
            original_idev: getCookie('idev'),
        },
        localStorage: {
            idev_id: localStorage.getItem(STORAGE_KEYS.IDEV_ID),
            idev_username: localStorage.getItem(STORAGE_KEYS.IDEV_USERNAME),
            customer_ip: localStorage.getItem(STORAGE_KEYS.CUSTOMER_IP),
            ip_data: localStorage.getItem(STORAGE_KEYS.IP_DATA),
        },
        ipTracking: {
            formExists: !!document.getElementById('idevform'),
            ipField: document.getElementById('idev_custom_x21') as HTMLInputElement,
            ipValue: (document.getElementById('idev_custom_x21') as HTMLInputElement)?.value,
        },
        location: {
            hostname: window.location.hostname,
            search: window.location.search,
        },
    }

    return allData
}

// Create tracking form with IP
const createTrackingForm = (ip: string) => {
    try {
        if (!ip) {
            console.warn('Attempted to create tracking form with empty IP')
            return
        }

        // Remove any existing tracking form
        const existingForm = document.getElementById('idevform')
        if (existingForm) {
            existingForm.remove()
        }

        // Create new form
        const form = document.createElement('form')
        form.id = 'idevform'
        form.style.display = 'none'

        // Add IP input
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'idev_custom_x21'
        input.id = 'idev_custom_x21' // Add ID for easier access
        input.value = ip
        form.appendChild(input)

        // Add form to document
        document.body.appendChild(form)

        // Verify form was created correctly
        const verifyForm = document.getElementById('idevform')
        const verifyInput = document.getElementById('idev_custom_x21') as HTMLInputElement

        if (!verifyForm || !verifyInput || verifyInput.value !== ip) {
            console.error('Form creation verification failed')
        }
    } catch (error) {
        console.error('Error creating tracking form:', error)
    }
}

// Track initial IP with debounce
let trackingInProgress = false
const trackInitialIp = async (idev_id: string) => {
    try {
        // Prevent duplicate calls
        if (trackingInProgress) {
            return null
        }
        trackingInProgress = true

        // Build the tracking URL
        const trackingUrl = new URL('https://affiliate.commzone.app/affiliate/connect/stripe_ip.php')
        trackingUrl.searchParams.append('idev_id', idev_id)
        trackingUrl.searchParams.append('REMOTE_ADDR', '') // Let the server determine the IP
        trackingUrl.searchParams.append('HTTP_X_FORWARDED_FOR', '') // Let the server determine the IP

        // Make the request
        const response = await fetch(trackingUrl.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                Referer: 'https://commzone.app',
            },
        })

        // Try to get IP from response body
        const text = await response.text()
        let ip = ''

        // Look for IP in response body (it might be embedded in HTML or as plain text)
        const ipMatch = text.match(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/)
        if (ipMatch) {
            ip = ipMatch[0]
        }

        // If no IP in body, try headers
        if (!ip) {
            ip = response.headers.get('X-Real-IP') || response.headers.get('X-Forwarded-For')?.split(',')[0] || ''
        }

        if (ip) {
            // Store IP in localStorage
            localStorage.setItem(STORAGE_KEYS.CUSTOMER_IP, ip)
            sessionStorage.setItem(STORAGE_KEYS.CUSTOMER_IP, ip)

            // Store IP in cookie for cross-domain access
            setCookie('affiliate_ip', ip)

            // Create tracking form
            createTrackingForm(ip)

            // Store full IP data
            const ipData = {
                ip,
                timestamp: new Date().toISOString(),
                affiliate_id: idev_id,
            }
            localStorage.setItem(STORAGE_KEYS.IP_DATA, JSON.stringify(ipData))
        } else {
            console.warn('No IP found in response')
        }

        return ip
    } catch (error) {
        console.error('Error tracking initial IP:', error)
        return null
    } finally {
        setTimeout(() => {
            trackingInProgress = false
        }, 1000)
    }
}

export const storeAffiliateData = async () => {
    try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const urlIdevId = urlParams.get('idev_id')
        const urlIdevUsername = urlParams.get('idev_username')

        // Get existing stored values
        const storedIdevId = getCookie('idev_id')
        const storedIdevUsername = getCookie('idev_username')

        // Get values from original idev cookie
        const originalIdevCookie = getCookie('idev')
        const cookieIdevId = originalIdevCookie ? originalIdevCookie.split('-')[0] : null

        // URL params take precedence over stored values
        const finalIdevId = urlIdevId || storedIdevId || cookieIdevId || ''
        const finalIdevUsername = urlIdevUsername || storedIdevUsername || ''

        // If this is a new affiliate ID from URL params, track the IP
        if (finalIdevId && urlIdevId && (!storedIdevId || urlIdevId !== storedIdevId)) {
            await trackInitialIp(finalIdevId)
        }

        // Get IP from cookie and restore to localStorage if needed
        const cookieIp = getCookie('affiliate_ip')
        if (cookieIp && !localStorage.getItem(STORAGE_KEYS.CUSTOMER_IP)) {
            localStorage.setItem(STORAGE_KEYS.CUSTOMER_IP, cookieIp)
            sessionStorage.setItem(STORAGE_KEYS.CUSTOMER_IP, cookieIp)
        }

        // Get current stored IP for logging
        const storedIp = localStorage.getItem(STORAGE_KEYS.CUSTOMER_IP) || cookieIp

        // Store the values if they exist
        if (finalIdevId) {
            setCookie('idev_id', finalIdevId)
        }
        if (finalIdevUsername) {
            setCookie('idev_username', finalIdevUsername)
        }

        // Verify storage after setting
        verifyAffiliateData()

        return {
            idev_id: finalIdevId || null,
            idev_username: finalIdevUsername || null,
            customer_ip: storedIp || null,
        }
    } catch (error) {
        console.error('Error storing affiliate data:', error)
        return {
            idev_id: null,
            idev_username: null,
            customer_ip: null,
        }
    }
}

export const getAffiliateData = () => {
    try {
        // Verify data accessibility before getting values
        verifyAffiliateData()

        const idev_id = getCookie('idev_id')
        const idev_username = getCookie('idev_username')

        // Get IP from storage
        const customer_ip = localStorage.getItem(STORAGE_KEYS.CUSTOMER_IP)

        return {
            idev_id: idev_id || null,
            idev_username: idev_username || null,
            customer_ip: customer_ip || null,
        }
    } catch (error) {
        console.error('Error getting affiliate data:', error)
        return {
            idev_id: null,
            idev_username: null,
            customer_ip: null,
        }
    }
}
